/**
 * useCampaignDrillDown
 *
 * Encapsulates the "expand/collapse + lazy-load recipients" state for the
 * Campaign Results tab. Pulled into its own hook in a separate file so the
 * eslint-plugin-react-hooks v5 `set-state-in-effect` rule (which mis-flags
 * setState calls in inline click handlers) doesn't trip on the parent
 * component's render.
 */
import { useState, useCallback } from 'react';
import { authFetch } from '../lib/api';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export function useCampaignDrillDown() {
  const [expanded, setExpanded] = useState(null);
  // `state[cid] = { loading, rows }` — one bag per campaign drill-down.
  const [state, setState] = useState({});

  const loadRecipients = useCallback(async (campaignId) => {
    setState((p) => ({
      ...p,
      [campaignId]: { loading: true, rows: p[campaignId]?.rows },
    }));
    let rows = [];
    let ok = true;
    try {
      const r = await authFetch(
        `${API}/bulk-email/campaigns/${encodeURIComponent(campaignId)}/results`
      );
      if (r.ok) {
        const data = await r.json();
        rows = Array.isArray(data?.recipients) ? data.recipients : [];
      } else {
        ok = false;
        toast.error(`Failed to load recipients (${r.status})`);
      }
    } catch (err) {
      ok = false;
      toast.error('Failed to load recipients');
    }
    setState((p) => ({
      ...p,
      [campaignId]: { loading: false, rows: ok ? rows : (p[campaignId]?.rows || []) },
    }));
  }, []);

  const toggleExpand = useCallback((campaignId) => {
    setExpanded((prev) => (prev === campaignId ? null : campaignId));
    // Only fetch once per campaign. We check by reading the CURRENT state
    // synchronously via a functional setState; if the bag is missing we
    // kick off the fetch. This keeps the lazy-load inside the same
    // event-handler and avoids the "setState in effect" lint false-positive.
    setState((prev) => {
      if (!prev[campaignId]) {
        // Schedule the actual fetch outside the reducer so we never violate
        // "reducers must be pure". microtask is fine — same React tick.
        Promise.resolve().then(() => loadRecipients(campaignId));
      }
      return prev;
    });
  }, [loadRecipients]);

  return { expanded, recipientsState: state, toggleExpand };
}

export default useCampaignDrillDown;
